import { useState, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';

/**
 * Custom hook for responsive design
 * Provides breakpoint detection and responsive utilities
 */
export const useResponsive = () => {
  const theme = useTheme();
  
  // MUI breakpoint queries
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // < 900px
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg')); // 900px - 1200px
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg')); // >= 1200px
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm')); // < 600px
  const isExtraSmall = useMediaQuery('(max-width: 480px)'); // Custom breakpoint for very small screens
  
  // Sidebar state for mobile
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  
  // Screen size state
  const [screenSize, setScreenSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight
  });
  
  // Update screen size on resize
  useEffect(() => {
    const handleResize = () => {
      setScreenSize({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Close mobile drawer when screen becomes desktop
  useEffect(() => {
    if (!isMobile) {
      setMobileDrawerOpen(false);
    }
  }, [isMobile]);
  
  // Responsive drawer width
  const getDrawerWidth = () => {
    if (isExtraSmall) return Math.min(280, screenSize.width * 0.85);
    if (isSmallMobile) return 300;
    return 320;
  };
  
  // Responsive grid breakpoints
  const getGridBreakpoints = (mobile = 12, tablet = 6, desktop = 4) => ({
    xs: mobile,
    sm: isSmallMobile ? mobile : tablet,
    md: tablet,
    lg: desktop,
    xl: desktop
  });
  
  // Responsive typography variants
  const getResponsiveTypography = (desktopVariant, tabletVariant, mobileVariant) => {
    if (isMobile) return mobileVariant || 'body2';
    if (isTablet) return tabletVariant || 'body1';
    return desktopVariant || 'h6';
  };
  
  // Responsive spacing
  const getResponsiveSpacing = (desktop = 3, tablet = 2, mobile = 1) => {
    if (isMobile) return mobile;
    if (isTablet) return tablet;
    return desktop;
  };
  
  // Responsive card height
  const getResponsiveCardHeight = (desktop = '120px', tablet = '100px', mobile = '80px') => {
    if (isMobile) return mobile;
    if (isTablet) return tablet;
    return desktop;
  };
  
  // Responsive dialog props
  const getResponsiveDialogProps = () => ({
    fullScreen: isSmallMobile,
    maxWidth: isMobile ? 'sm' : 'lg',
    fullWidth: true,
    PaperProps: {
      sx: {
        margin: isMobile ? 1 : 3,
        width: isMobile ? 'calc(100% - 16px)' : 'auto',
        maxHeight: isMobile ? 'calc(100vh - 16px)' : '90vh'
      }
    }
  });
  
  // Responsive app bar height
  const getAppBarHeight = () => {
    return isSmallMobile ? 56 : 64;
  };
  
  // Responsive container padding
  const getContainerPadding = () => {
    if (isExtraSmall) return 1;
    if (isSmallMobile) return 2;
    if (isMobile) return 3;
    return 4;
  };
  
  // Toggle mobile drawer
  const toggleMobileDrawer = () => {
    setMobileDrawerOpen(prev => !prev);
  };
  
  // Close mobile drawer
  const closeMobileDrawer = () => {
    setMobileDrawerOpen(false);
  };
  
  return {
    // Breakpoint booleans
    isMobile,
    isTablet,
    isDesktop,
    isSmallMobile,
    isExtraSmall,
    
    // Screen dimensions
    screenSize,
    
    // Drawer state
    mobileDrawerOpen,
    toggleMobileDrawer,
    closeMobileDrawer,
    
    // Responsive utilities
    getDrawerWidth,
    getGridBreakpoints,
    getResponsiveTypography,
    getResponsiveSpacing,
    getResponsiveCardHeight,
    getResponsiveDialogProps,
    getAppBarHeight,
    getContainerPadding,
    
    // Theme breakpoints for direct use
    theme
  };
};

export default useResponsive;
